// core/services/firebase/followService.ts
import { 
  collection, 
  doc, 
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
  updateDoc
} from 'firebase/firestore';
import { db } from './config';
import { socialService } from './socialService';
import { notificationService } from './notificationService';
import { counterService } from './counterService'; // Added

export interface Follow {
  id: string;
  followerId: string;      // Handle de quem segue
  followingId: string;     // Handle de quem é seguido
  createdAt: Date;
}

export const followService = {
  /**
   * Follow a user/business
   */
  async follow(followerId: string, followingId: string): Promise<void> {
    const followId = `${followerId}_${followingId}`;
    const followRef = doc(db, 'follows', followId);
    
    await setDoc(followRef, {
      followerId,
      followingId,
      createdAt: Timestamp.now()
    });

    // Update follower's following count
    await counterService.incrementCounter(`profiles/${followerId}`, 'stats.following', 1);

    // Update following's followers count
    await counterService.incrementCounter(`profiles/${followingId}`, 'stats.followers', 1);

    // Create notification
    try {
      const followerProfile = await socialService.getProfileByHandle(followerId);
      const followingProfile = await socialService.getProfileByHandle(followingId);
      
      if (followerProfile && followingProfile && followingProfile.userId) {
        await notificationService.createNotification(followingProfile.userId, {
          type: 'follow',
          fromUserId: followerProfile.userId || followerId,
          fromUserHandle: followerId,
          fromUserName: followerProfile.displayName,
          fromUserAvatar: followerProfile.avatarUrl,
          targetId: followerId,
          targetType: 'profile',
          message: 'começou a seguir você'
        });
      }
    } catch (err) {
      console.error('Failed to create follow notification:', err);
    }
  },

  /**
   * Unfollow a user/business
   */
  async unfollow(followerId: string, followingId: string): Promise<void> {
    const followId = `${followerId}_${followingId}`;
    const followRef = doc(db, 'follows', followId);
    
    await deleteDoc(followRef);

    // Update follower's following count
    await counterService.incrementCounter(`profiles/${followerId}`, 'stats.following', -1);

    // Update following's followers count
    await counterService.incrementCounter(`profiles/${followingId}`, 'stats.followers', -1);
  },

  /**
   * Check if user is following another user
   */
  async isFollowing(followerId: string, followingId: string): Promise<boolean> {
    const followId = `${followerId}_${followingId}`;
    const followRef = doc(db, 'follows', followId);
    const snapshot = await getDoc(followRef);
    return snapshot.exists();
  },

  /**
   * Get list of followers for a user
   */
  async getFollowers(userId: string, maxFollowers: number = 100): Promise<Follow[]> {
    const q = query(
      collection(db, 'follows'),
      where('followingId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxFollowers)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as Follow));
  },

  /**
   * Get list of users that a user is following
   */
  async getFollowing(userId: string, maxFollowing: number = 100): Promise<Follow[]> {
    const q = query(
      collection(db, 'follows'),
      where('followerId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxFollowing)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as Follow));
  }
};
